import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Icon,
  Input,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  Text,
} from "@salestrak/ui";

import type {
  StepFieldControlProps,
  StepUIDef,
} from "#app/components/automations/canvas/step-ui-registry";
import {
  FieldRow,
  FieldEntitySelect,
} from "#app/components/automations/trigger-sentence";
import {
  filterVariableGroupsByType,
  type VariableGroup,
} from "#app/components/automations/canvas/automation-variables";
import {
  VariableTextInput,
  InsertVariableMenu,
} from "#app/components/automations/canvas/variable-text-input";
import {
  UNSETTABLE_FIELD_TYPES,
  updateFieldVariableTypes,
} from "#app/components/automations/field-capabilities";
import { useCustomFieldPool } from "#app/components/entity-selectors";
import { CustomFieldRenderer } from "#app/components/custom-fields/custom-field-renderers";
import { useCustomFieldsList } from "#app/lib/hooks/use-custom-fields-list";
import { connectionRows } from "#app/lib/data";
import {
  buildCustomFieldDefMap,
  getInitialCustomFieldValue,
  type CustomFieldDef,
} from "#app/lib/custom-field-utils";

// ── "Update field" value editing ────────────────────────────────────────────
// A value can be a literal, a relative date (date fields only), or a variable
// template. Relative dates serialize to `{{now}}` / `{{now+3d}}` / `{{now-3d}}`.
const RELATIVE_OPTIONS = [
  { value: "now", label: "Now (run time)" },
  { value: "after", label: "Days after now" },
  { value: "before", label: "Days before now" },
];
const NOW_RE = /^\{\{\s*now\s*\}\}$/i;
const NOW_OFFSET_RE = /^\{\{\s*now\s*([+-])\s*(\d+)d\s*\}\}$/i;

// How each custom-field type behaves as an "update field" value:
//   freeform — single-line chip input (literal text + type-compatible variables)
//   static   — only its typed control, no variables
//   date     — specific / relative / date variable
//   paragraph→ multiline chip input; member/address → typed control only
// The insert-variable picker is filtered per target type — a number field only
// offers number/money variables (updateFieldVariableTypes, mirrored in the
// server's publish gate) — so an automation can't be authored into a value
// that fails type validation on every run.
const FREEFORM_VALUE_TYPES = new Set([
  "text",
  "number",
  "money",
  "email",
  "phone",
  "url",
]);
// Address is static too: its value is an object only its full editor can
// build — a chip/text input would produce a string the API always rejects.
const STATIC_VALUE_TYPES = new Set(["select", "checkbox", "address"]);
const DATE_VALUE_TYPES = new Set(["date", "time"]);

type ValueMode = "literal" | "relative" | "variable";

function detectValueMode(value: unknown): ValueMode {
  if (typeof value === "string") {
    if (NOW_RE.test(value) || NOW_OFFSET_RE.test(value)) return "relative";
    if (value.includes("{{")) return "variable";
  }
  return "literal";
}

function parseRelative(value: unknown): { dir: "now" | "after" | "before"; days: number } {
  if (typeof value === "string") {
    const m = NOW_OFFSET_RE.exec(value);
    if (m) return { dir: m[1] === "+" ? "after" : "before", days: Number(m[2]) };
  }
  return { dir: "now", days: 0 };
}

function serializeRelative(dir: "now" | "after" | "before", days: number): string {
  if (dir === "now" || !days) return "{{now}}";
  return dir === "after" ? `{{now+${days}d}}` : `{{now-${days}d}}`;
}

// Value editor for "Update field": the typed value control with an "Insert
// variable" button below it. Inserting a variable flips the value into the
// chip editor (so it reads as chips, not raw `{{ }}`); date fields also offer a
// relative "now ± N days" builder. Mode follows the stored value, with an
// explicit override so typing a literal in the chip editor doesn't bounce back.
function UpdateFieldValueEditor({
  field,
  value,
  onChange,
  variableGroups,
}: {
  field: CustomFieldDef;
  value: unknown;
  onChange: (value: unknown) => void;
  variableGroups: VariableGroup[];
}) {
  const type = field.type;
  const supportsRelative = DATE_VALUE_TYPES.has(type);
  const [override, setOverride] = useState<ValueMode | null>(null);
  // Only variables the target type can actually hold — empty groups hide the
  // insert affordance entirely (e.g. member/address take none).
  const compatibleGroups = filterVariableGroupsByType(
    variableGroups,
    updateFieldVariableTypes(type),
  );

  // Freeform fields are always the chip text input (compatible variables
  // allowed inline).
  if (FREEFORM_VALUE_TYPES.has(type)) {
    return (
      <VariableTextInput
        value={value == null ? "" : String(value)}
        onChange={onChange}
        groups={compatibleGroups}
        placeholder={
          compatibleGroups.length ? "Enter a value or insert variables" : "Enter a value"
        }
      />
    );
  }
  // Paragraph (textarea): the multiline chip editor.
  if (type === "paragraph") {
    return (
      <VariableTextInput
        value={value == null ? "" : String(value)}
        onChange={onChange}
        groups={compatibleGroups}
        placeholder="Enter text or insert variables"
        multiline
      />
    );
  }
  // Dropdown / checkbox: only their typed control, no variables.
  if (STATIC_VALUE_TYPES.has(type)) {
    return (
      <CustomFieldRenderer
        field={field}
        value={value ?? null}
        onChangeValue={onChange}
        surface="default"
        size="lg"
      />
    );
  }

  // Date-like and member: typed control, plus insert-variable when compatible
  // variables exist (date also adds the relative builder). Member fields take
  // no variables — the runner needs a member array no template can produce —
  // so they render the typed selector alone.
  const mode = override ?? detectValueMode(value);
  const rel = parseRelative(value);

  const useFixedValue = () => {
    setOverride("literal");
    onChange(getInitialCustomFieldValue(field) ?? null);
  };

  if (mode === "variable") {
    return (
      <Box gap="xs">
        <VariableTextInput
          value={typeof value === "string" ? value : ""}
          onChange={onChange}
          groups={compatibleGroups}
          placeholder="Enter text or insert variables"
        />
        <Box direction="row" justify="start">
          <Button variant="ghost" size="xs" onClick={useFixedValue}>
            <Text size="xs" variant="muted">
              Use a fixed value
            </Text>
          </Button>
        </Box>
      </Box>
    );
  }

  if (mode === "relative") {
    return (
      <Box gap="xs">
        <Box direction="row" align="center" gap="sm">
          <Box fill="flex">
            {/* Select is value-keyed: an item's value is a plain string. */}
            <Select
              onValueChange={(next: string | null) => {
                // Clearing the direction means "no offset", i.e. "now".
                const dir = (next || "now") as "now" | "after" | "before";
                onChange(serializeRelative(dir, dir === "now" ? 0 : rel.days || 1));
              }}
              value={rel.dir}
            >
              <SelectTrigger size="lg">
                {/* Base UI's Value renders the raw value ("now") without a
                    formatter, not the item's label ("Now (run time)"). */}
                <SelectValue placeholder="When">
                  {(selected: string | null) =>
                    RELATIVE_OPTIONS.find((o) => o.value === selected)?.label ??
                    "When"
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectPopup>
                {RELATIVE_OPTIONS.map((o, i) => (
                  <SelectItem key={`${o.value}-${i}`} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          </Box>
          {rel.dir !== "now" ? (
            <>
              <Box width={88}>
                <Input
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const t = event.target.value;
                    onChange(
                      serializeRelative(
                        rel.dir,
                        t === "" ? 0 : Math.max(0, Math.floor(Number(t) || 0)),
                      ),
                    );
                  }}
                  placeholder="Days"
                  size="lg"
                  value={String(rel.days)}
                />
              </Box>
              <Text size="sm" variant="muted">
                days
              </Text>
            </>
          ) : null}
        </Box>
        <Box direction="row" justify="start">
          <Button variant="ghost" size="xs" onClick={useFixedValue}>
            <Text size="xs" variant="muted">
              Use a specific date
            </Text>
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box gap="xs">
      <CustomFieldRenderer
        field={field}
        value={value ?? null}
        onChangeValue={onChange}
        surface="default"
        size="lg"
      />
      {compatibleGroups.length || supportsRelative ? (
        <Box direction="row" align="center" justify="start" gap="2xs">
          <InsertVariableMenu
            groups={compatibleGroups}
            align="start"
            onSelect={(item) => {
              setOverride("variable");
              onChange(`{{ ${item.path} }}`);
            }}
          />
          {supportsRelative ? (
            <Button
              variant="ghost"
              size="xs"
              onClick={() => {
                setOverride("relative");
                onChange("{{now}}");
              }}
            >
              <Icon name="clock" size="xs" />
              <Text size="xs" variant="muted">
                Relative date
              </Text>
            </Button>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

// The whole "Update field" block: the field picker (choosing a field seeds a
// type-correct initial value so the value control below renders immediately)
// paired with the typed value editor. Owns both schema fields — `fieldKey`
// renders here and `value` is registered "hidden".
function UpdateFieldControl({
  field,
  config,
  variableGroups,
  onConfigChange,
}: StepFieldControlProps) {
  // "Update field" can only set types its value editor can express — files/
  // photos/region are hidden from its picker (see field-capabilities.ts).
  const { pool: settableFieldPool } = useCustomFieldPool({
    excludeTypes: UNSETTABLE_FIELD_TYPES,
  });
  // The value control is typed by the CHOSEN field, so we need its full
  // definition (not just the id the entityRef picker stores).
  const { data: cfData } = useCustomFieldsList(false);
  const cfDefMap = useMemo(
    () => buildCustomFieldDefMap(connectionRows(cfData?.customFields)),
    [cfData],
  );

  const fieldId =
    typeof config[field.key] === "string" ? (config[field.key] as string) : null;
  const def = fieldId ? cfDefMap[fieldId] : null;

  return (
    <Box direction="column" gap="md">
      <FieldRow label={field.label} required={field.required}>
        <FieldEntitySelect
          pools={[settableFieldPool]}
          value={fieldId}
          placeholder={`Select ${field.label.toLowerCase()}`}
          size="lg"
          onChange={(id) => {
            if (!id) {
              onConfigChange({ ...config, fieldKey: undefined, value: undefined });
              return;
            }
            const next = cfDefMap[id];
            onConfigChange({
              ...config,
              fieldKey: id,
              value: next ? getInitialCustomFieldValue(next) : undefined,
            });
          }}
        />
      </FieldRow>
      {def ? (
        <FieldRow label="Value">
          <UpdateFieldValueEditor
            key={fieldId ?? "none"}
            field={def}
            value={config.value ?? null}
            onChange={(v) => onConfigChange({ ...config, value: v })}
            variableGroups={variableGroups}
          />
        </FieldRow>
      ) : null}
    </Box>
  );
}

export const updateFieldStepUI: StepUIDef = {
  label: "Update Custom Field",
  group: "{subject} MANAGEMENT",
  fields: {
    fieldKey: UpdateFieldControl,
    // Rendered by UpdateFieldControl alongside the field picker.
    value: "hidden",
  },
};
